import { useCallback, useMemo, useRef, useState } from 'react';

export function useVolumesSoloMute() {
  const [volumes, setVolumes] = useState<Record<string, number>>({});
  const [muted, setMuted] = useState<Record<string, boolean>>({});
  const [solo, setSolo] = useState<Record<string, boolean>>({});
  const lastNonZeroVolRef = useRef<Record<string, number>>({});

  const anySolo = useMemo(() => Object.values(solo).some(Boolean), [solo]);
  const getEffectiveGain = useCallback((role: string) => {
    const base = volumes[role] ?? 1;
    const isMuted = !!muted[role] || base <= 0 || (anySolo && !solo[role]);
    return isMuted ? 0 : base;
  }, [volumes, muted, anySolo, solo]);

  const setVolume = useCallback((role: string, value: number) => {
    setVolumes(v => ({ ...v, [role]: value }));
    if (value > 0) { lastNonZeroVolRef.current[role] = value; }
    setMuted(m => ({ ...m, [role]: value <= 0 }));
  }, []);

  const toggleMute = useCallback((role: string) => {
    setMuted(m => {
      const next = !m[role];
      setVolumes(v => {
        if (next) return { ...v, [role]: 0 };
        const restore = lastNonZeroVolRef.current[role] ?? 1; return { ...v, [role]: restore };
      });
      return { ...m, [role]: next };
    });
  }, []);

  const toggleSolo = useCallback((role: string) => { setSolo(s => ({ ...s, [role]: !s[role] })); }, []);

  return { volumes, muted, solo, setVolume, toggleMute, toggleSolo, getEffectiveGain } as const;
}
